import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Offer, Quote, QuoteSettings } from '@app/quoting/models/quote';
import { FetchQuote, QuotingModuleActionTypes, SetQuote } from '@quoting/state/actions/quoting-module.actions';
import { QuoteRequestState } from '@app/shared';
import { LoadingModalComponent, LoadingModalData } from '@app/shared/components/loading-modal/loading-modal.component';
import { QuoteRequestService } from '@app/shared/services/quote-request.service';
import { UtilityHubIdentity } from '@auth/models/utility-hub-identity';
import { Store } from '@ngrx/store';
import { AppState } from '@app/state/app.state';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { QuoteRequestStatus } from '@app/shared/models/quote-request-status';
import { ActivatedRoute } from '@angular/router';
import { QuoteService } from '@app/quoting/services/quote.service';
import { QuoteRequestType } from '@app/shared/models/QuoteRequestType';
import LogRocket from 'logrocket';
import { QuoteSettingsService } from '@app/quoting/services/quote-settings.service';
import { ContractLength } from '@app/shared/models/contract-length';
import { MarginTerm } from '@app/quoting/models/quote-request-summary';
import { convertPriceToMarginTerm, sortAscendingByProperty } from '@app/shared/helpers/array-helper';
import { SetContractLengthsSelected, SetMarginDollar, SetMarginTerm, SetShowShareQuoteButton } from '@utility-information/state/actions/utility-information.actions';
import { marginTermSelector } from '@utility-information/state/selectors/utility-information.selectors';
import { configurationSelector, isReadOnlySelector, loggedInUtilityHubIdentitySelector, quoteConfigurationSelector, utilityAccountsSelector } from '@app/state/app.selectors';
import { UtilityHubConfiguration } from '../../../configuration/models/utilityhub-configuration';
import { QuoteHelperService } from '../../services/quote-helper.service';
import { QuotationDeactivationCheckerService } from '@app/shared/services/quotation-deactivation-checker/quotation-deactivation-checker.service';


@Component({
  selector: 'app-quote',
  templateUrl: './quote.component.html',
  styleUrls: ['./quote.component.scss'],
  providers: [QuotationDeactivationCheckerService],
})
export class QuoteComponent implements OnInit, OnDestroy, OnChanges {
  @Input() quoteSettings: QuoteSettings;
  @Input() waitForLoadProfileCall: boolean;
  @Input() quoteRequestState: QuoteRequestState;
  @Input() inputAnnualUsage: number;
  @Input() contractLengths: number[];


  
  // public configuration: UtilityHubConfiguration;
  // private selectUtilityHubConfiguration$: Subscription;

  @Output() offersActioned = new EventEmitter<QuotingModuleActionTypes>();

  @ViewChild('settings') settings;

  quoteRequestId: number;
  quote: Quote;
  generatingQuote: boolean;
  configuration: UtilityHubConfiguration;
  userIdentity: UtilityHubIdentity;
  isGetQuotePageValid: boolean;
  requestAQuoteButtonPressedAndPageInvalid: boolean = true;
  uhIdentity$: Subscription;
  quoteSettings$: Subscription;
  route$: Subscription;
  getQuoteRequestState$: Subscription;
  sendQuoteRequest$: Subscription;
  selectUtilityHubConfiguration$: Subscription;
  dialog$: Subscription;
  upliftDollar$: Observable<number>;
  marginTerm$: Observable<MarginTerm[]>;
  marginTerm: Array<MarginTerm> = [];
  isAllMarginChanged: boolean;
  newContractStartDates: string[];
  isReadOnly$: Subscription;
  isReadOnly: boolean;

  constructor(
    private quoteRequestService: QuoteRequestService,
    private state: Store<AppState>,
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private quoteService: QuoteService,
    private store: Store<AppState>,
    private quoteSettingsService: QuoteSettingsService,
    public quoteHelperService: QuoteHelperService,
    public quotationDeactivationCheckerService: QuotationDeactivationCheckerService,
  ) {
    this.marginTerm$ = this.store.select(marginTermSelector);
    this.quoteService.quoteRequestStateUpdated.subscribe((x) => {
      this.quoteRequestState = x;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['quoteRequestState']?.currentValue) this.quoteRequestState = changes['quoteRequestState'].currentValue;
    if (changes?.contractLengths) this.contractLengths = changes?.contractLengths.currentValue;
    if (changes['waitForLoadProfileCall']?.currentValue) this.waitForLoadProfileCall = changes['waitForLoadProfileCall'].currentValue;
  }

  ngOnDestroy(): void {
    this.uhIdentity$?.unsubscribe();
    this.quoteSettings$?.unsubscribe();
    this.route$?.unsubscribe();
    this.getQuoteRequestState$?.unsubscribe();
    this.dialog$?.unsubscribe();
    this.sendQuoteRequest$?.unsubscribe();
    this.selectUtilityHubConfiguration$?.unsubscribe();
    this.isReadOnly$?.unsubscribe();
  }

  ngOnInit(): void {
    this.route$ = this.route.params.subscribe((x) => {
      if (!isNaN(x.quoteRequestId)) {
        this.quoteRequestId = Number(x.quoteRequestId);
      }
    });

    this.quoteService.quoteUpdated.subscribe((x) => {
      this.quote = x;
    });

    this.uhIdentity$ = this.state.select(loggedInUtilityHubIdentitySelector).subscribe((user) => {
      this.userIdentity = user;
    });

    this.selectUtilityHubConfiguration$ = this.store
      .select(configurationSelector)
      .subscribe((config) => {
        this.configuration = config;
    });

    combineLatest([
      this.store.select(utilityAccountsSelector),
      this.store.select(quoteConfigurationSelector)
    ]).subscribe(([utilityAccounts, quoteConfiguration]) => {
      this.newContractStartDates = this.quoteHelperService.determineContractStartDates(this.quote, quoteConfiguration, utilityAccounts);
    });

    this.isReadOnly$ = this.store.select(isReadOnlySelector).subscribe((isReadOnly) => {
      this.isReadOnly = isReadOnly;
    });
  }

  public marginChanged(ismarginEdited: boolean)
  {
    this.isAllMarginChanged = ismarginEdited;
  }

  public getQuote(quoteSettings: QuoteSettings) {
    this.generatingQuote = true;
    this.quoteSettings = quoteSettings;
    this.state.dispatch(new FetchQuote());

    LogRocket.track('request_quote', { quoteRequestType: this.quoteRequestState?.quoteRequestType });

    this.marginTerm$.subscribe((x) => { this.marginTerm = x; }).unsubscribe();

    this.dialog$ = this.dialog
      .open(LoadingModalComponent, {
        width: '25%',
        disableClose: true,
        data: {
          message: `Requesting a Quote`,
          unitOfWork: this.quoteRequestService.generateQuote(this.quoteRequestId, {
            quoteDueDate: quoteSettings.quoteDueDate,
            marginDollar: quoteSettings.marginDollar ?? 0,
            upliftDollar: quoteSettings.upliftDollar ?? 0,
            standardPriceOnly: this.quoteRequestState?.quoteRequestType === QuoteRequestType.Matrix,
            isTaxIncluded: quoteSettings.isTaxIncluded,
            marginTerm: this.marginTerm,
            agentFee: quoteSettings.agentFee
          }),
        } as LoadingModalData,
      })
      .afterClosed()
      .subscribe(
        (results) => {
          this.generatingQuote = false;
          if (results.results && results.quoteRequestState) {
            const offersWithNoResult = results.results?.filter((x) => x.noResult);
            let offersWithResults = results.results
            ?.filter((x) => !x.noResult);

            offersWithResults = sortAscendingByProperty(offersWithResults, 'marginTerm$');

            const sortedOffers = (offersWithResults || []).concat(offersWithNoResult);
            if (!results.error) {
              LogRocket.track('quote_returned', { quoteRequestType: this.quoteRequestState.quoteRequestType });
              this.quote = {
                prices: sortedOffers,
                quoteDate: results.generatedDate,
                quoteRequestState: results.quoteRequestState,
                hasIDontKnows: results.quoteRequestState.hasIDontKnows,
                marginDollar: quoteSettings.marginDollar ?? 0,
                upliftDollar: quoteSettings.upliftDollar ?? 0,
                inputAnnualUsage: this.inputAnnualUsage,
                pricingCurveDate: results.pricingCurveDate,
                isTaxIncluded: quoteSettings.isTaxIncluded
              };
              this.store.dispatch(new SetShowShareQuoteButton(true));
              this.store.dispatch(new SetContractLengthsSelected(this.quoteRequestId.toString(), sortedOffers?.map((x) => x.term)));
              let marginTerm = convertPriceToMarginTerm( this.quote.prices as Offer[]);
              const customMargin = quoteSettings.marginDollar ?? 0;
              if (customMargin !== 0) {
                marginTerm = marginTerm.map(mt =>
                  mt.isSweetSpot && mt.margin === 0 ? { ...mt, margin: customMargin } : mt
                );
              }
              this.store.dispatch(new SetMarginTerm(this.quoteRequestId.toString(), marginTerm));

              this.uhIdentity$ = this.state.select(loggedInUtilityHubIdentitySelector).subscribe((user) => {
                this.state.dispatch(new SetQuote(this.quote, user, results.quoteRequestState.hasIDontKnows));
                this.state.dispatch(
                  new SetContractLengthsSelected(
                    this.quoteRequestId.toString(),
                    sortedOffers.map((x) => ({ months: x.term, selected: true } as ContractLength))
                  )
                );

                this.quoteSettingsService.updateOfferFilters({
                  contractLengths: offersWithResults.map((p) => p.term as number),
                  includeSweetSpot: true,
                });
              });
            } else {
              this.dispatchQuoteError(results);
            }
          } else {
            this.dispatchQuoteError(results);
          }
        },
        (err) => {
          this.generatingQuote = false;
          this.dispatchQuoteError({
            error: {
              ...err,
            },
          });
        }
      );
  }

  public setGetQuotePageValid(isValid: boolean) {
    this.isGetQuotePageValid = isValid;
    if (!this.isGetQuotePageValid) {
      this.requestAQuoteButtonPressedAndPageInvalid = true;
    }
  }

  public updateQuoteSettings(quoteSettings: QuoteSettings) {
    this.quoteSettings = quoteSettings;
  }

  public onOffersActioned(actionTaken: QuotingModuleActionTypes): void {
    this.offersActioned.emit(actionTaken);
  }

  public sendQuoteRequestToSupplier(event) {
    if (!this.isGetQuotePageValid) {
      this.requestAQuoteButtonPressedAndPageInvalid = true;
    } else {
      this.requestAQuoteButtonPressedAndPageInvalid = false;
      this.state.dispatch(new SetQuote(this.quote, this.userIdentity, this.quote.hasIDontKnows));
      this.state.dispatch(new SetMarginDollar(this.quote?.quoteRequestState?.quoteRequestId.toString(), this.quote.marginDollar));
      this.sendQuoteRequest$ = this.quoteRequestService
        .sendQuoteRequestToSupplier(this.quoteRequestId, {
          ...this.quoteSettings,
          standardPriceOnly: this.quoteRequestState.quoteRequestType === QuoteRequestType.Matrix,
          brokerageExternalId: this.userIdentity.currentOrganisation.externalId,
        })
        .subscribe((res) => {
          window.location.reload();
        });
    }
  }

  public showRequestAQuoteButton() {
    return (
      this.quoteRequestState?.hasIDontKnows &&
      this.userIdentity?.isBroker &&
      this.quoteRequestState?.status !== QuoteRequestStatus.QuoteRequested
    );
  }

  private dispatchQuoteError(results: any) {
    const quote = {
      error: {
        error: results.error.message,
        detail: "",
      },
      quoteRequestState: this.quoteRequestState,
      prices: [],
    };
    this.uhIdentity$ = this.state.select(loggedInUtilityHubIdentitySelector).subscribe((user) => {
      LogRocket.track('quote_errored', { results });
      this.state.dispatch(new SetQuote(quote, user, null));
    });
  }

  public isQuotingDisabled(): boolean {
    return this.quoteHelperService.IsQuotationDisabled(this.configuration.configuration, this.newContractStartDates).isDisabled;
  }
}
